<template>
  <div
    ref="resultCardRef"
    :class="classes"
    @click="$emit('select', careProvider.zsrNumber)"
    @mouseover="$emit('mouseover', careProvider.zsrNumber)"
    @mouseout="$emit('mouseout', careProvider.zsrNumber)"
  >
    <div class="result-card__data">
      <div class="result-card__address">
        <div
          class="title-2"
          v-html="careProvider.name"
        />
        <div
          class="body-2"
          v-html="careProvider.address"
        />
      </div>
      <div
        v-if="discountGroup"
        class="caption-regular result-card__discount"
        v-html="discountText"
      />
    </div>
    <div
      v-if="showPracticingDoctors"
      class="result-card__practicing-doctors"
    >
      <div
        class="caption-bold"
        v-text="$t('careProvider.practicingDoctors.title')"
      />
      <div
        v-for="(practicingDoctor, index) in careProvider.practicingDoctors.list"
        :key="index"
        class="caption-regular"
        v-text="practicingDoctor"
      />
      <div
        v-if="careProvider.practicingDoctors.total > TOTAL_OF_DISPLAYED_DOCTORS"
        class="caption-regular"
        v-html="$t('careProvider.practicingDoctors.total', { number: careProvider.practicingDoctors.total - TOTAL_OF_DISPLAYED_DOCTORS })"
      />
    </div>
    <div
      v-if="!showMessage"
      :class="hintClasses"
    >
      <the-icon
        art="light"
        :name="AVAILABILITY_HINT[status].icon"
      />
      <div
        class="body-3"
        v-text="$t(`careProvider.status.${status}.title`)"
      />
    </div>
    <basic-message
      v-else
      :severity="AVAILABILITY_HINT[status].severity"
      :closeable="false"
      :title-label="$t(`careProvider.status.${status}.title`)"
      :content="$t(`careProvider.status.${status}.text`)"
    />
    <div
      v-if="isSelected"
      class="result-card__actions"
    >
      <basic-button
        :label="addButtonLabel"
        @click.stop="$emit('add', careProvider.zsrNumber)"
      />
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, useTemplateRef, watch } from 'vue'

  import { BasicButton, BasicMessage } from '@shared/components/Basic'

  import useI18n from '@shared/hooks/useI18n'

  import { AVAILABILITY_HINT } from '@shared/components/CareProvider/constants'

  // HOOKS
  const { t } = useI18n()

  // INIT
  const TOTAL_OF_DISPLAYED_DOCTORS = 5

  defineEmits(['add', 'mouseover', 'mouseout', 'select'])
  const props = defineProps({
    autoScroll: {
      type: Boolean,
      default: false,
    },

    avmNetwork: {
      type: String,
      required: true,
    },

    careProvider: {
      type: Object,
      required: true,
    },

    hovered: {
      type: [String, null],
      default: null,
    },

    isAlternativeAvmNetwork: {
      type: Boolean,
      default: false,
    },

    product: {
      type: Object,
      default: () => undefined,
    },

    selected: {
      type: [String, null],
      default: null,
    },

    showDetails: {
      type: Boolean,
      default: false,
    }
  })

  // DATA
  const isHovered = ref(false)
  const isSelected = ref(false)
  const resultCardRef = useTemplateRef('resultCardRef')

  // COMPUTED
  const addButtonLabel = computed(() => {
    return props.isAlternativeAvmNetwork
      ? t(`careProvider.result.card.${props.avmNetwork}.switch`)
      : t(`careProvider.add`)
  })

  const classes = computed(() => {
    return {
      'result-card': true,
      'result-card--selected': isSelected.value,
      'result-card--hovered': isHovered.value && !isSelected.value,
      'appearance-extended': isSelected.value,
    }
  })

  const discountGroup = computed(() => {
    return props.careProvider.discounts[props.avmNetwork]
  })

  const discountText = computed(() => {
    const prefix = t('careProvider.discount')
    const value = t(`careProvider.discountGroup.${discountGroup.value}.text`)

    return `${prefix}: ${value}`
  })

  const hintClasses = computed(() => {
    return {
      'result-card__hint': true,
      [`result-card__hint--${status.value}`]: true
    }
  })

  const showMessage = computed(() => {
    return isSelected.value && status.value !== 'ACCEPTS_ALL'
  })

  const showPracticingDoctors = computed(() => {
    return (props.showDetails || isSelected.value) && props.careProvider.practicingDoctors.list
  })

  const status = computed(() => {
    // all statuses are considered ACCEPT_ALL except ACCORDING_FAMILY_DOCTOR and ACCEPTS_NOBODY
    const unavailableStatuses = ['ACCORDING_FAMILY_DOCTOR', 'ACCEPTS_NOBODY']
    return unavailableStatuses.includes(props.careProvider.newPatientStatus)
      ? props.careProvider.newPatientStatus
      : 'ACCEPTS_ALL'
  })

  // METHODS
  function scrollToCard() {
    const offsetTop = resultCardRef.value.getBoundingClientRect().top
    const offsetBottom = resultCardRef.value.offsetHeight
    const parentElement = resultCardRef.value.parentElement
    const parentNode = parentElement?.parentNode

    if (offsetTop < 0 || offsetTop > parentNode.offsetHeight - offsetBottom) {
      resultCardRef.value.scrollIntoView({
        behavior: 'instant',
        block: 'center',
      })
    }

  }

  // WATCHERS
  watch(
    () => props.hovered,
    (val) => {
      isHovered.value = val === props.careProvider.zsrNumber
    }
  )

  watch(
    () => props.selected,
    (val) => {
      isSelected.value = val === props.careProvider.zsrNumber
      if (isSelected.value) {
        scrollToCard()
      }
    }
  )

</script>

<style scoped>
.result-card {
  display: flex;
  flex-direction: column;
  row-gap: var(--fixed-spacing-fix-04);
  padding: var(--fixed-spacing-fix-04) var(--fixed-spacing-fix-02);
  background-color: var(--surface);
  border-bottom: 1px solid var(--on-surface);
  transition: background-color 0.15s ease-in;
  cursor: pointer;
}

.result-card__data {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
  column-gap: var(--fixed-spacing-fix-04);
}

.result-card__hint {
  display: flex;
  flex-direction: row;
  column-gap: var(--fixed-spacing-fix-02);
  align-items: center;
  transition: color 0.15s ease-in;
}

.result-card__hint--ACCEPTS_ALL {
  color: var(--secondary);
}

.result-card__hint--ACCORDING_FAMILY_DOCTOR {
  color: var(--attention);
}

.result-card__hint--ACCEPTS_NOBODY {
  color: var(--error);
}

.result-card--hovered {
  background-color: var(--surface-ext-b-medium);
}

.result-card--hovered .result-card__hint--ACCEPTS_ALL {
  color: var(--on-secondary-container);
}

.result-card--hovered .result-card__hint--ACCORDING_FAMILY_DOCTOR {
  color: var(--on-attention-container);
}

.result-card--hovered .result-card__hint--ACCEPTS_NOBODY {
  color: var(--on-error-container);
}

.result-card--selected {
  background-color: var(--surface-ext-b-low);
}

.result-card__practicing-doctors {
  display: flex;
  flex-direction: column;
}

.result-card__discount {
  flex: 1 0 auto;
  text-align: right;
}

@media (--v-medium) {
  .result-card__actions {
    display: flex;
    justify-content: flex-end;
  }
}

</style>
